import {
  Table,
  // Divider,
  Select,
  Button,
  Checkbox,
} from "antd";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import "./index.css";

const { Option } = Select;

const ProductProfitabilityTables = () => {
  const { productProfitabilityData, loading } = useSelector(
    (state) => state.productProfitabilityReducer
  );

  const [showDecreasing, setShowDecreasing] = useState(false);
  const [showIncreasing, setShowIncreasing] = useState(false);

  const handleDecreasingChange = (e) => {
    setShowDecreasing(e.target.checked);
    if (e.target.checked) setShowIncreasing(false);
  };

  const handleIncreasingChange = (e) => {
    setShowIncreasing(e.target.checked);
    if (e.target.checked) setShowDecreasing(false);
  };

  const dataSource = useMemo(() => {
    let data = productProfitabilityData.map((item, idx) => ({
      ...item,
      key: idx,
    }));

    if (showDecreasing) {
      data = data.filter((record) => {
        const avg30 = Number(record.avg30Days);
        const avg7 = Number(record.avg7Days);
        if (
          avg30 == null ||
          avg7 == null ||
          isNaN(avg30) ||
          isNaN(avg7) ||
          avg30 === 0
        ) {
          return false;
        }
        const percentChange = ((avg7 - avg30) / avg30) * 100;
        return percentChange > 10;
      });
    } else if (showIncreasing) {
      data = data.filter((record) => {
        const avg30 = Number(record.avg30Days);
        const avg7 = Number(record.avg7Days);
        if (
          avg30 == null ||
          avg7 == null ||
          isNaN(avg30) ||
          isNaN(avg7) ||
          avg30 === 0 ||
          avg7 === 0
        ) {
          return false;
        }
        const percentChange = ((avg7 - avg30) / avg30) * 100;
        return percentChange < -10;
      });
    }
    return data;
  }, [productProfitabilityData, showDecreasing, showIncreasing]);

  // Вземи всички уникални имена на продукти
  const productNames = useMemo(
    () =>
      Array.from(
        new Set(productProfitabilityData.map((item) => item.itemName))
      ),
    [productProfitabilityData]
  );

  // Вземи всички уникални категории
  const categoryNames = useMemo(
    () =>
      Array.from(
        new Set(productProfitabilityData.map((item) => item.categoryName))
      ),
    [productProfitabilityData]
  );

  const columns = [
    {
      title: "Продукт",
      dataIndex: "itemName",
      key: "itemName",
      align: "center",
      width: "10%",
      filters: productNames.map((name) => ({
        text: name,
        value: name,
      })),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Select
            showSearch
            placeholder="Избери продукт"
            value={selectedKeys[0]}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            style={{ width: 200, marginBottom: 8, display: "block" }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            allowClear
          >
            {productNames.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          <div className="product-profitability-columns-filter-container">
            <Button
              type="primary"
              size="small"
              className="product-profitability-columns-filter-action-btn"
              onClick={confirm}
            >
              Филтрирай
            </Button>
            <Button
              size="small"
              className="product-profitability-columns-filter-reset-btn"
              onClick={clearFilters}
            >
              Изчисти
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <span
          role="img"
          aria-label="search"
          style={{
            color: filtered ? "#1890ff" : undefined,
          }}
        >
          🔍
        </span>
      ),
      onFilter: (value, record) => record.itemName === value,
    },
    {
      title: "Категория",
      dataIndex: "categoryName",
      key: "categoryName",
      align: "center",
      filters: categoryNames.map((name) => ({
        text: name,
        value: name,
      })),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Select
            showSearch
            placeholder="Избери категория"
            value={selectedKeys[0]}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            style={{ width: 200, marginBottom: 8, display: "block" }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            allowClear
          >
            {categoryNames.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          <div className="product-profitability-columns-filter-container">
            <Button
              type="primary"
              size="small"
              className="product-profitability-columns-filter-action-btn"
              onClick={confirm}
            >
              Филтрирай
            </Button>
            <Button
              size="small"
              className="product-profitability-columns-filter-reset-btn"
              onClick={clearFilters}
            >
              Изчисти
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <span
          role="img"
          aria-label="search"
          style={{
            color: filtered ? "#1890ff" : undefined,
          }}
        >
          🔍
        </span>
      ),
      onFilter: (value, record) => record.categoryName === value,
    },
    {
      title: "Продадено",
      dataIndex: "sold",
      key: "sold",
      align: "center",
      sorter: (a, b) => (a.sold ?? 0) - (b.sold ?? 0),
      sortDirections: ["ascend", "descend"],
      render: (val) => val ?? "-",
    },
    {
      title: (
        <span>
          Последните
          <br />
          30 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
            цена / %
          </span> */}
        </span>
      ),
      key: "avg30Days",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percent30Days ?? -Infinity);
        const bVal = Number(b.percent30Days ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.avg30Days != null && !isNaN(record.avg30Days)
        //     ? Number(record.avg30Days).toFixed(2)
        //     : "-";
        const percent =
          record.percent30Days != null ? record.percent30Days + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          Последните
          <br />7 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
            цена / %
          </span> */}
        </span>
      ),
      key: "avg7Days",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percent7Days ?? -Infinity);
        const bVal = Number(b.percent7Days ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.avg7Days != null && !isNaN(record.avg7Days)
        //     ? Number(record.avg7Days).toFixed(2)
        //     : "-";
        const percent =
          record.percent7Days != null ? record.percent7Days + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          -6 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
            цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus6",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus6 ?? -Infinity);
        const bVal = Number(b.percentMinus6 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus6 != null && !isNaN(record.dayMinus6)
        //     ? Number(record.dayMinus6).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus6 != null ? record.percentMinus6 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          -5 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus5",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus5 ?? -Infinity);
        const bVal = Number(b.percentMinus5 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus5 != null && !isNaN(record.dayMinus5)
        //     ? Number(record.dayMinus5).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus5 != null ? record.percentMinus5 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          -4 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus4",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus4 ?? -Infinity);
        const bVal = Number(b.percentMinus4 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus4 != null && !isNaN(record.dayMinus4)
        //     ? Number(record.dayMinus4).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus4 != null ? record.percentMinus4 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },

    {
      title: (
        <span>
          -3 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus3",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus3 ?? -Infinity);
        const bVal = Number(b.percentMinus3 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus3 != null && !isNaN(record.dayMinus3)
        //     ? Number(record.dayMinus3).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus3 != null ? record.percentMinus3 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          -2 дни
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus2",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus2 ?? -Infinity);
        const bVal = Number(b.percentMinus2 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus2 != null && !isNaN(record.dayMinus2)
        //     ? Number(record.dayMinus2).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus2 != null ? record.percentMinus2 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          -1 ден
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          цена / %
          </span> */}
        </span>
      ),
      key: "dayMinus1",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentMinus1 ?? -Infinity);
        const bVal = Number(b.percentMinus1 ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.dayMinus1 != null && !isNaN(record.dayMinus1)
        //     ? Number(record.dayMinus1).toFixed(2)
        //     : "-";
        const percent =
          record.percentMinus1 != null ? record.percentMinus1 + "%" : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          Вчера
          {/* <br />
          <span style={{ fontWeight: "normal", fontSize: "12px" }}>
            цена / %
          </span> */}
        </span>
      ),
      key: "referenceDay",
      align: "center",
      sorter: (a, b) => {
        const aVal = Number(a.percentReferenceDay ?? -Infinity);
        const bVal = Number(b.percentReferenceDay ?? -Infinity);
        return aVal - bVal;
      },
      sortDirections: ["ascend", "descend"],
      render: (record) => {
        // const avg =
        //   record.referenceDay != null && !isNaN(record.referenceDay)
        //     ? Number(record.referenceDay).toFixed(2)
        //     : "-";
        const percent =
          record.percentReferenceDay != null
            ? record.percentReferenceDay + "%"
            : "-";
        return (
          <span>
            {/* <span>{avg}</span>
            <Divider style={{ margin: "2px 0" }} /> */}
            <span style={{ fontWeight: "bold" }}>{percent}</span>
          </span>
        );
      },
    },
  ];

  return (
    <div className="product-profitability-table-container">
      <div className="product-profitability-table-header">
        <div className="product-profitability-legend-container">
          <div className="product-profitability-legend">
            <span className="product-profitability-legend-item">
              <span className="product-profitability-legend-dot-green"></span>
              <span className="product-profitability-legend-label">
                Ако % са нараснали с 10% или повече
              </span>
            </span>
            <span className="product-profitability-legend-item">
              <span className="product-profitability-legend-dot-red"></span>
              <span className="product-profitability-legend-label">
                Ако % са намалели с 10% или повече
              </span>
            </span>
          </div>
        </div>
        <div className="product-profitability-checkbox-group">
          <Checkbox checked={showDecreasing} onChange={handleDecreasingChange}>
            покажи само с повишаващи %
          </Checkbox>
          <Checkbox checked={showIncreasing} onChange={handleIncreasingChange}>
            покажи само с намаляващи %
          </Checkbox>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        size="middle"
        className="product-profitability-table"
        rowClassName={(record) => {
          const avg30 = Number(record.avg30Days);
          const avg7 = Number(record.avg7Days);
          if (
            avg30 == null ||
            avg7 == null ||
            isNaN(avg30) ||
            isNaN(avg7) ||
            avg30 === 0 ||
            avg7 === 0
          ) {
            return ""; // Без оцветяване ако няма стойност или деление на 0
          }
          const percentChange = ((avg7 - avg30) / avg30) * 100;
          if (percentChange > 10) return "row-green";
          if (percentChange < -10) return "row-red";
          return "";
        }}
      />
    </div>
  );
};

export default ProductProfitabilityTables;
